import { request } from '@umijs/max';
import type { ApiPageResult, ApiSortItem } from '@/services/apiResponse';
import type { CreditPoolPlanUsage } from '@/services/inferencePro/creditPool';
import { getNebulithInferenceProApiUrl } from '@/services/util';
import type { PlanEmbedded, PlanType } from './plan';
export type PlanInstanceStatus =
  | 'pending_payment'
  | 'using'
  | 'cancelled'
  | 'expired';

export type PlanInstanceCreditType = 'trial' | 'credit' | 'postpaid';

export type PlanInstanceCreditStatus =
  | 'active'
  | 'exhausted'
  | 'expired'
  | 'destroyed';

/** 套餐实例当前生效额度包（详情接口） */
export interface PlanInstanceCredit {
  billingTenantId?: string;
  creditGrantId?: string;
  creditType?: PlanInstanceCreditType | string;
  effectiveEndAt?: string;
  effectiveStartAt?: string;
  naturalEndAt?: string;
  planId?: string;
  planInstanceId?: string;
  remainingAmount?: string;
  remainingAmountMicro?: number;
  status?: PlanInstanceCreditStatus | string;
  totalAmount?: string;
  totalAmountMicro?: number;
  userId?: string;
}

/** 套餐实例（列表/详情接口结构一致） */
export interface PlanInstanceListItem {
  billingTenantId?: string;
  canManualRenew?: boolean;
  /** 当前生效额度包列表（额度总览 Token 包数量） */
  credits?: PlanInstanceCredit[];
  effectiveEndAt?: string;
  effectiveStartAt?: string;
  instanceId?: string;
  nextRenewAt?: string;
  plan?: Partial<PlanEmbedded>;
  planId?: string;
  planInstanceId: string;
  planInstanceNo?: string;
  planType?: PlanType | string;
  purchasePriceAmount?: number;
  purchasePriceAmountYuan?: string;
  status?: PlanInstanceStatus | string;
  usage?: CreditPoolPlanUsage;
  userId?: string;
  user?: {
    displayName?: string;
    email?: string;
    status?: string;
    userId?: string;
    username?: string;
  };
  tenant?: {
    description?: string;
    displayName?: string;
    status?: string;
    tenantId?: string;
  };
}

export type PlanInstanceDetail = PlanInstanceListItem;

export interface PlanInstanceDetailParams {
  planInstanceId: string;
}

export interface PlanInstanceListPageParams {
  billingTenantId?: string;
  createAtEnd?: number;
  createAtStart?: number;
  planId?: string;
  planName?: string;
  planType?: PlanType | string;
  status?: PlanInstanceStatus | string;
  userId?: string;
  page: {
    pageNo: number;
    pageSize: number;
  };
  sorts?: ApiSortItem[];
}

export type PlanInstanceListPageResult = ApiPageResult<PlanInstanceListItem>;

export interface PlanInstanceCancelParams {
  cancelReason?: string;
  planInstanceId: string;
}

export interface PlanInstanceRenewParams {
  planInstanceId: string;
}

export type PlanInstanceUsageRecordBusinessEvent =
  | 'create'
  | 'consume'
  | 'autoReset'
  | 'manualReset'
  | 'inactive';

export type PlanInstanceUsageRecordOperationType =
  | 'create'
  | 'consume'
  | 'reset'
  | 'inactive';

export type PlanInstanceUsageRecordGrantStatus =
  | 'active'
  | 'exhausted'
  | 'inactive'
  | 'pending_effective';

export interface PlanInstanceUsageRecordInferenceModel {
  contextWindow?: number;
  description?: string;
  dimension?: number;
  gatewayModelUuid?: string;
  inferenceModelId?: string;
  modelName?: string;
  modelTypeId?: string;
  status?: string;
}

export interface PlanInstanceUsageRecordItem {
  businessEvent?: PlanInstanceUsageRecordBusinessEvent | string;
  createAt?: string;
  creditGrantHistoryId?: string;
  creditGrantId?: string;
  inferenceModel?: PlanInstanceUsageRecordInferenceModel | null;
  inferenceModelId?: string;
  operationRemark?: string;
  operationType?: PlanInstanceUsageRecordOperationType | string;
  planId?: string;
  planInstanceId?: string;
  statusAfter?: PlanInstanceUsageRecordGrantStatus | string;
  statusBefore?: PlanInstanceUsageRecordGrantStatus | string | null;
  updateAt?: string;
  usedAmountAfter?: string;
  usedAmountBefore?: string;
  usedAmountMicroAfter?: number;
  usedAmountMicroBefore?: number;
}

export interface PlanInstanceUsageRecordListPageParams {
  planInstanceId: string;
  page: {
    pageNo: number;
    pageSize: number;
  };
  sorts?: ApiSortItem[];
}

export type PlanInstanceUsageRecordListPageResult =
  ApiPageResult<PlanInstanceUsageRecordItem>;

/** 套餐实例分页列表（订阅管理） */
export async function getPlanInstanceListPage(
  params: PlanInstanceListPageParams,
  options?: { skipErrorHandler?: boolean },
) {
  return request<PlanInstanceListPageResult>(
    getNebulithInferenceProApiUrl('plan-instance/list-page'),
    {
      method: 'POST',
      data: params,
      ...(options || {}),
    },
  );
}

/** 套餐实例详情 */
export async function getPlanInstanceDetail(
  params: PlanInstanceDetailParams,
  options?: { skipErrorHandler?: boolean },
) {
  return request<PlanInstanceDetail>(
    getNebulithInferenceProApiUrl('plan-instance/detail'),
    {
      method: 'POST',
      data: params,
      ...(options || {}),
    },
  );
}

/** 取消套餐实例订阅 */
export async function cancelPlanInstance(
  params: PlanInstanceCancelParams,
  options?: { skipErrorHandler?: boolean },
) {
  return request<unknown>(
    getNebulithInferenceProApiUrl('plan-instance/cancel'),
    {
      method: 'POST',
      data: params,
      ...(options || {}),
    },
  );
}

/** 手动续费套餐实例 */
export async function renewPlanInstance(
  params: PlanInstanceRenewParams,
  options?: { skipErrorHandler?: boolean },
) {
  return request<unknown>(
    getNebulithInferenceProApiUrl('plan-instance/renew'),
    {
      method: 'POST',
      data: params,
      ...(options || {}),
    },
  );
}

/** 套餐实例额度流水分页列表 */
export async function getPlanInstanceUsageRecordListPage(
  params: PlanInstanceUsageRecordListPageParams,
  options?: { skipErrorHandler?: boolean },
) {
  return request<PlanInstanceUsageRecordListPageResult>(
    getNebulithInferenceProApiUrl('plan-instance/usage-record/list-page'),
    {
      method: 'POST',
      data: params,
      ...(options || {}),
    },
  );
}
